import React from 'react';
import { Alert, Button } from 'antd';
import { ThunderboltOutlined, ToolOutlined, CloudSyncOutlined } from '@ant-design/icons';
import { TRIAGE_ACTIONS } from '../../types/triage';
import type { BatteryStatus, Ticket, TriageActionDef } from '../../types/triage';

interface Suggestion {
  message: string;
  type: 'error' | 'warning' | 'info';
  actionKey: string;
  icon: React.ReactNode;
}

interface SuggestedActionBannerProps {
  batteryStatus: BatteryStatus | null;
  ticket?: Ticket | null;
  nodeDetail?: any | null;
  onAction?: (action: TriageActionDef) => Promise<void>;
}

function getSuggestions(batteryStatus: BatteryStatus | null, ticket?: Ticket | null, nodeDetail?: any | null): Suggestion[] {
  const suggestions: Suggestion[] = [];

  // Battery-based suggestions. The "% remaining" is a mAh-consumption forecast
  // that freezes the moment a node goes silent, so for a long-offline node it
  // extrapolates a misleadingly healthy figure (the 1.89V-but-"94% remaining"
  // contradiction). Drop the "% remaining" clause once the node is stale and
  // say so; the voltage reading stays because it's a real measurement.
  const offlineDays = nodeDetail?.last_seen
    ? Math.floor((Date.now() - new Date(nodeDetail.last_seen).getTime()) / 86400000)
    : null;
  const forecastStale = offlineDays != null && offlineDays >= 7;
  if (batteryStatus?.suggested_action) {
    const action = batteryStatus.suggested_action.toLowerCase();
    const v = Number(batteryStatus.avg_battery_lvl).toFixed(2);
    const pctClause = forecastStale
      ? `${v}V, % remaining stale — ${offlineDays}d silent`
      : `${v}V, ${Math.round(Number(batteryStatus.estimated_battery_left_perc))}% remaining`;
    if (action.startsWith('replace node')) {
      suggestions.push({
        message: `Battery analysis suggests: Replace Node (${pctClause})`,
        type: 'error',
        actionKey: 'hardware',
        icon: <ToolOutlined />,
      });
    } else if (action.startsWith('change batteries')) {
      suggestions.push({
        message: `Battery analysis suggests: Change Batteries (${pctClause})`,
        type: 'warning',
        actionKey: 'hardware_eol',
        icon: <ThunderboltOutlined />,
      });
    }
  }

  // (Removed a dead weak-signal suggestion that read `nodeDetail.signal_strength
  // < -85`: sensorflow.nodes has no `signal_strength` column, so the branch
  // never fired, and its dBm/-85 assumption was wrong anyway — the fleet's
  // signal metric is a positive LQI-style score, higher=better. Weak-signal /
  // out-of-range routing is handled by the classifier, see WEAK_SIGNAL_MIN.)

  // Age-based suggestion: tickets older than 60 days with no action
  if (ticket && !ticket.action_id) {
    const days = Math.floor((Date.now() - new Date(ticket.ticket_created).getTime()) / 86400000);
    if (days >= 60) {
      suggestions.push({
        message: `Ticket is ${days} days old with no action — consider on-site investigation`,
        type: 'info',
        actionKey: 'on_site',
        icon: <CloudSyncOutlined />,
      });
    }
  }

  return suggestions;
}

const SuggestedActionBanner: React.FC<SuggestedActionBannerProps> = ({ batteryStatus, ticket, nodeDetail, onAction }) => {
  const suggestions = getSuggestions(batteryStatus, ticket, nodeDetail);
  if (suggestions.length === 0) return null;

  return (
    <>
      {suggestions.map((suggestion, i) => {
        const triageAction = TRIAGE_ACTIONS.find((a) => a.key === suggestion.actionKey);
        return (
          <Alert
            key={i}
            type={suggestion.type}
            showIcon
            icon={suggestion.icon}
            message={
              <span style={{ fontSize: 12, wordBreak: 'break-word' }}>{suggestion.message}</span>
            }
            style={{ marginBottom: 12 }}
            action={
              triageAction && onAction ? (
                <Button
                  size="small"
                  type="primary"
                  danger={suggestion.type === 'error'}
                  onClick={() => onAction(triageAction)}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {triageAction.shortLabel}
                </Button>
              ) : undefined
            }
          />
        );
      })}
    </>
  );
};

export default SuggestedActionBanner;
